import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePageTitle } from '../../hooks/usePageTitle';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { Mail, ArrowLeft, Send, Star } from 'lucide-react';

const ForgotPasswordPage: React.FC = () => {
  usePageTitle('Recuperar Senha');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast.error('Por favor, informe seu email');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Por favor, informe um email válido');
      return;
    }

    setIsLoading(true);

    try {
      await api.post('/auth/forgot-password', { email });
      setEmailSent(true);
      toast.success('Email enviado! Verifique sua caixa de entrada.');
    } catch (error: any) {
      console.error('Erro ao solicitar recuperação:', error);
      toast.success('Se o email existir, você receberá instruções para recuperação');
      setEmailSent(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen paper-dark flex flex-col relative overflow-hidden font-body">
      {/* Decorative sparkles */}
      <Star className="sparkle w-8 h-8 text-mostarda" style={{ top: '8%', left: '10%' }} fill="currentColor" />
      <Star className="sparkle w-5 h-5 text-creme" style={{ top: '20%', right: '12%', animationDelay: '0.6s' }} fill="currentColor" />
      <Star className="sparkle w-10 h-10 text-mostarda" style={{ bottom: '12%', right: '8%', animationDelay: '1.1s' }} fill="currentColor" />
      <Star className="sparkle w-6 h-6 text-creme" style={{ bottom: '18%', left: '14%', animationDelay: '1.6s' }} fill="currentColor" />

      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-md w-full">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-creme/85 hover:text-mostarda transition-colors text-sm font-semibold mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para o login
          </Link>

          {/* Header */}
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-creme border-2 border-tinta rounded-full flex items-center justify-center"
              style={{ boxShadow: '4px 4px 0 0 #1A1A1A' }}>
              <Mail className="h-7 w-7 text-bordo" />
            </div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl font-black text-creme leading-tight">
              Esqueceu sua <span className="italic text-mostarda">senha</span>?
            </h1>
            <p className="mt-3 text-creme/85 italic">
              {emailSent
                ? 'Verifique seu email para continuar'
                : 'Informe seu email para receber instruções de recuperação'}
            </p>
          </div>

          {emailSent ? (
            <div className="space-y-6">
              <div className="card-vintage rounded-2xl p-7">
                <div className="text-center space-y-4">
                  <div className="mx-auto h-16 w-16 bg-musgo border-2 border-tinta rounded-full flex items-center justify-center"
                    style={{ boxShadow: '3px 3px 0 0 #1A1A1A' }}>
                    <Send className="h-8 w-8 text-creme" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-tinta">
                    Email enviado com sucesso!
                  </h3>
                  <p className="text-tinta-soft">
                    Se existe uma conta cadastrada com o email <strong className="text-bordo">{email}</strong>,
                    você receberá instruções para redefinir sua senha.
                  </p>
                  <div className="bg-mostarda/20 border-l-4 border-mostarda-dark p-4 mt-4 rounded-r-lg text-left">
                    <p className="text-sm text-tinta">
                      <strong>Importante:</strong> Verifique também sua caixa de spam.
                      O link expira em 1 hora.
                    </p>
                  </div>
                </div>
              </div>

              <div className="text-center space-y-3">
                <button
                  onClick={() => {
                    setEmailSent(false);
                    setEmail('');
                  }}
                  className="text-sm font-bold text-mostarda hover:text-mostarda-light underline italic"
                >
                  Enviar para outro email
                </button>
                <div>
                  <Link
                    to="/"
                    className="inline-flex items-center text-sm font-semibold text-creme/85 hover:text-mostarda"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Voltar para login
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <form className="space-y-5" onSubmit={handleSubmit}>
              <div className="card-vintage rounded-2xl p-7">
                <label htmlFor="email" className="block text-sm font-bold text-tinta mb-1.5 uppercase tracking-wider">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="input-vintage block w-full px-4 py-3 rounded-lg text-base"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                />
              </div>

              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-vintage-mustard w-full flex items-center justify-center py-3.5 px-4 rounded-lg text-base"
                >
                  {isLoading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-tinta"></div>
                  ) : (
                    <>
                      <Send className="h-5 w-5 mr-2" />
                      Enviar link de recuperação
                    </>
                  )}
                </button>

                <Link
                  to="/"
                  className="btn-vintage-light w-full flex items-center justify-center py-3.5 px-4 rounded-lg text-base"
                >
                  <ArrowLeft className="h-5 w-5 mr-2" />
                  Voltar para login
                </Link>
              </div>

              <div className="text-center pt-2">
                <p className="text-sm text-creme/85">
                  Lembrou sua senha?{' '}
                  <Link
                    to="/"
                    className="font-bold text-mostarda hover:text-mostarda-light underline"
                  >
                    Fazer login
                  </Link>
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
